import React, { useState, useContext } from 'react';
// import { SlimLayout } from '../../common/SlimLayout'; // Removed SlimLayout
import logo from '../../../assets/images/logo.jpeg';
import OnboardingService from '../../../services/OnboardingService';
import { useNavigate } from 'react-router-dom';
import { ChatSupportContext } from '../../../contexts/ChatSupportContext';

export const metadata = {
  title: 'Audit-AI Registration',
};

export default function Register() {
  const navigate = useNavigate();
  const { chatSupportRef } = useContext(ChatSupportContext);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    company: '',
    referrer: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showExistingUserModal, setShowExistingUserModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate first name
    if (!formData.first_name) {
      newErrors.first_name = 'First name is required';
    } else if (formData.first_name.length < 2) {
      newErrors.first_name = 'First name must be at least 2 characters';
    }
    
    // Validate last name
    if (!formData.last_name) {
      newErrors.last_name = 'Last name is required';
    } else if (formData.last_name.length < 2) {
      newErrors.last_name = 'Last name must be at least 2 characters';
    }
    
    // Validate email
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    
    // Validate company
    if (!formData.company) {
      newErrors.company = 'Company name is required';
    } else if (formData.company.length < 2) {
      newErrors.company = 'Company name must be at least 2 characters';
    }
    
    // Validate referrer
    if (!formData.referrer) {
      newErrors.referrer = 'Please select how you heard about us';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      console.log('Submitting form data:', formData);
      const register = await OnboardingService.createRegistration({
        FIRST_NAME: formData.first_name,
        LAST_NAME: formData.last_name,
        COMPANY: formData.company,
        REFERRER: formData.referrer,
        EMAIL: formData.email,
      });

      if (register) {
        setShowSuccessModal(true);
        await OnboardingService.sendRegistrationConfirmation({
          FIRST_NAME: formData.first_name,
          LAST_NAME: formData.last_name,
          COMPANY: formData.company,
          REFERRER: formData.referrer,
          EMAIL: formData.email,
        });
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      if (error.message === 'USER_EXISTS') {
        console.log('User exists error triggered, showing existing user modal');
        setShowExistingUserModal(true);
      } else {
        setErrorMessage('There was a problem with the registration. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactClick = (e) => {
    e.preventDefault();
    // Open the chat without specifying a category
    if (chatSupportRef && chatSupportRef.current) {
      chatSupportRef.current.openChat();
    }
  };

  return (
    // Removed SlimLayout wrapper
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-blue-50/10 relative overflow-hidden">
      {/* Simplified background patterns */}
      <div className="absolute inset-0 z-0 overflow-hidden opacity-10">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-blue-50/10 opacity-70"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10 px-4 sm:px-6 lg:px-8 py-8">
        {/* Updated header styling to match main site */}
        <div className="flex justify-center mb-12">
          <a 
            href="/" 
            className="inline-flex items-center justify-center no-underline hover:no-underline"
          >
            <img 
              src={logo} 
              alt="Audit-AI" 
              className="h-14 w-auto drop-shadow-md" 
            />
            <span className="ml-3 text-2xl font-bold bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
              Audit-AI
            </span>
          </a>
        </div>

        {/* Two-column layout with simplified styling */}
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-start">
          {/* Left column - Product Showcase (simplified) */}
          <div className="w-full lg:w-5/12">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 relative overflow-hidden">
              {/* Dashboard Preview */}
              <div className="relative shadow-lg rounded-xl overflow-hidden border border-gray-100 mx-4 mt-4">
                {/* Dashboard Header */}
                <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-2 flex items-center justify-between">
                  <div className="flex space-x-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
                  </div>
                  <div className="text-white text-xs font-medium">AuditAI Dashboard</div>
                  <div className="px-2 py-0.5 rounded bg-white/20 text-white text-xs">Enterprise</div>
                </div>
                
                {/* Dashboard Content */}
                <div className="bg-white p-3">
                  {/* Stats Row */}
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {[
                      { color: 'bg-blue-50 border-blue-100', percent: '92%', label: 'Controls' },
                      { color: 'bg-green-50 border-green-100', percent: '100%', label: 'Coverage' },
                      { color: 'bg-indigo-50 border-indigo-100', percent: '-80%', label: 'Work' }
                    ].map((stat, i) => (
                      <div key={i} className={`${stat.color} p-2 rounded border`}>
                        <div className="text-sm font-bold text-gray-700">{stat.percent}</div>
                        <div className="text-xs text-gray-500">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Chart Element */}
                  <div className="h-14 mb-3 flex items-end space-x-1 p-1 bg-gray-50 rounded">
                    {[40, 25, 35, 60, 45, 75, 50, 65, 30, 55, 45, 70].map((height, i) => (
                      <div key={i} 
                        className="flex-1 bg-gradient-to-t from-indigo-500 to-blue-500 rounded-t"
                        style={{ height: `${height}%` }}
                      ></div>
                    ))}
                  </div>
                  
                  {/* Data Table */}
                  <div className="border border-gray-100 rounded text-xs mb-2">
                    <div className="flex bg-gray-50 p-1 border-b border-gray-100">
                      <div className="w-1/3 font-medium text-gray-600">Status</div>
                      <div className="w-1/3 font-medium text-gray-600">Date</div>
                      <div className="w-1/3 font-medium text-gray-600">Compliance</div>
                    </div>
                    <div className="flex p-1 border-b border-gray-50">
                      <div className="w-1/3 flex items-center">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1"></div>
                        <span className="text-gray-600">Approved</span>
                      </div>
                      <div className="w-1/3 text-gray-600">Today</div>
                      <div className="w-1/3 text-green-600">100%</div>
                    </div>
                    <div className="flex p-1">
                      <div className="w-1/3 flex items-center">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mr-1"></div>
                        <span className="text-gray-600">In Review</span>
                      </div>
                      <div className="w-1/3 text-gray-600">Yesterday</div>
                      <div className="w-1/3 text-blue-600">95%</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Feature Highlights - Simplified */}
              <div className="px-4 py-5">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Enterprise Compliance Automation</h3>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {[
                    {
                      icon: (
                        <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      ),
                      title: "Framework Compliant",
                      desc: "Automated audit coverage"
                    },
                    {
                      icon: (
                        <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      ),
                      title: "AI-Powered Analysis",
                      desc: "Intelligent risk detection"
                    },
                    {
                      icon: (
                        <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      ),
                      title: "Real-time Reporting",
                      desc: "Instant compliance status"
                    },
                    {
                      icon: (
                        <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                      ),
                      title: "80% Time Savings",
                      desc: "Reduced audit workload"
                    }
                  ].map((feature, i) => (
                    <div key={i} className="p-3 rounded-lg bg-white border border-gray-100 shadow-sm">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">{feature.icon}</div>
                        <div className="ml-2">
                          <div className="text-sm font-medium text-gray-800">{feature.title}</div>
                          <div className="text-xs text-gray-600">{feature.desc}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Trust Indicator - Simplified */}
              <div className="border-t border-gray-100 px-4 py-3 bg-slate-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="text-xs font-medium text-gray-700">Trusted by 10+ innovative teams</div>
                    <div className="flex space-x-0.5 ml-2">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-3 h-3 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right column - Registration form */}
          <div className="w-full lg:w-7/12">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="p-8">
                <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600 mb-4">
                  Schedule Your Enterprise Demo
                </h2>
                <p className="mt-2 text-gray-600 mb-6">
                  Discover how our intelligent platform can transform your compliance operations. Complete your information below to secure your personalized demonstration.
                </p>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name Fields */}
                  <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                        First Name
                      </label>
                      <input
                        type="text"
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        className={`block w-full px-4 py-3 rounded-xl border ${
                          errors.first_name ? 'border-red-500' : 'border-gray-200'
                              } shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white/90 backdrop-blur-sm`}
                      />
                      {errors.first_name && (
                        <div className="mt-1 text-sm text-red-600">{errors.first_name}</div>
                      )}
                    </div>
                    
                    <div>
                      <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        className={`block w-full px-4 py-3 rounded-xl border ${
                          errors.last_name ? 'border-red-500' : 'border-gray-200'
                              } shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white/90 backdrop-blur-sm`}
                      />
                      {errors.last_name && (
                        <div className="mt-1 text-sm text-red-600">{errors.last_name}</div>
                      )}
                    </div>
                  </div>

                  {/* Email Field */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                            Business Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`block w-full px-4 py-3 rounded-xl border ${
                        errors.email ? 'border-red-500' : 'border-gray-200'
                            } shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white/90 backdrop-blur-sm`}
                    />
                    {errors.email && (
                      <div className="mt-1 text-sm text-red-600">{errors.email}</div>
                    )}
                  </div>

                  {/* Company Field */}
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className={`block w-full px-4 py-3 rounded-xl border ${
                        errors.company ? 'border-red-500' : 'border-gray-200'
                            } shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white/90 backdrop-blur-sm`}
                    />
                    {errors.company && (
                      <div className="mt-1 text-sm text-red-600">{errors.company}</div>
                    )}
                  </div>

                  {/* Referrer Field */}
                  <div>
                    <label htmlFor="referrer" className="block text-sm font-medium text-gray-700 mb-1">
                      How did you hear about us?
                    </label>
                    <select
                      id="referrer"
                      name="referrer"
                      value={formData.referrer}
                      onChange={handleChange}
                      className={`block w-full px-4 py-3 rounded-xl border ${
                        errors.referrer ? 'border-red-500' : 'border-gray-200'
                      } shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none bg-no-repeat bg-right pr-10`}
                      style={{
                        backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
                        backgroundSize: "1.5em 1.5em"
                      }}
                    >
                      <option value="">Select an option</option>
                      <option value="linkedin">LinkedIn</option>
                      <option value="twitter">Twitter</option>
                      <option value="facebook">Facebook</option>
                      <option value="youtube">YouTube</option>
                      <option value="reddit">Reddit</option>
                      <option value="github">GitHub</option>
                      <option value="quora">Quora</option>
                      <option value="medium">Medium</option>
                      <option value="stack_overflow">Stack Overflow</option>
                      <option value="other">Other</option>
                    </select>
                    {errors.referrer && (
                      <div className="mt-1 text-sm text-red-600">{errors.referrer}</div>
                    )}
                  </div>

                  {/* Error Message */}
                  {errorMessage && (
                    <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
                      <div className="flex">
                        <svg className="h-5 w-5 text-red-400 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        {errorMessage}
                      </div>
                    </div>
                  )}

                  {/* Submit Button with updated styling to match header */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`relative w-full py-3 px-6 rounded-xl text-white font-medium ${
                      isSubmitting 
                        ? 'bg-indigo-400 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-indigo-600 to-blue-600 hover:shadow-lg transition-all duration-300'
                    }`}
                  >
                    <span className="flex items-center justify-center">
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </>
                      ) : (
                        <>
                          Book Your Demo
                          <svg className="ml-2 -mr-1 w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </>
                      )}
                    </span>
                  </button>
                </form>

                {/* Sign In Link */}
                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600">
                    Already registered?{' '}
                    <a
                      href="/login"
                      className="font-medium text-indigo-600 hover:text-indigo-500 transition-colors duration-150"
                    >
                      Sign in to your account
                    </a>
                  </p>
                </div>
              </div>
              
              {/* Enterprise Trust Indicators - simplified to match header style */}
              <div className="border-t border-gray-100 px-8 py-4 bg-slate-50">
                <div className="flex flex-wrap justify-center items-center gap-4">
                  <div className="flex items-center text-xs text-gray-500">
                    <svg className="w-4 h-4 mr-1 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Enterprise Security
                  </div>
                  <div className="flex items-center text-xs text-gray-500">
                    <svg className="w-4 h-4 mr-1 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    AI-Powered
                  </div>
                  <div className="flex items-center text-xs text-gray-500">
                    <svg className="w-4 h-4 mr-1 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Real-time Analytics
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 relative">
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Demo Request Confirmed
              </h3>
              
              <p className="text-gray-600 mb-8 text-base">
              Thank you for your interest in our enterprise platform. Our team will reach out within 24 hours to schedule your personalized demonstration and needs assessment.
              </p>

              <div className="w-full">
                <button
                  onClick={() => navigate('/login')}
                  className="w-full flex justify-center items-center rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-3 text-base font-medium text-white shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                  </svg>
                  Go to Login
                </button>
              </div>

              <div className="mt-6 text-sm text-gray-500">
                Need help? Contact us at{' '}
                <a 
                  href="mailto:support@auditai.com"
                  className="text-indigo-600 hover:text-indigo-500 transition-colors"
                >
                  support@auditai.com
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Existing User Modal */}
      {showExistingUserModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 md:p-8">
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">Account Already Exists</h3>
              <p className="mt-2 text-sm text-gray-500">
                An account with this email address already exists. Please sign in instead, or contact support if you need help accessing your account.
              </p>
              
              <div className="w-full space-y-4 mt-6">
                <button
                  onClick={handleContactClick}
                  className="w-full flex justify-center items-center rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-3 text-base font-medium text-white shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Contact Support
                </button>
                
                <button
                  onClick={() => {
                    setShowExistingUserModal(false);
                    setFormData(prev => ({ ...prev, email: '' }));
                  }}
                  className="w-full flex justify-center items-center rounded-xl bg-gray-100 px-6 py-3 text-base font-medium text-gray-700 hover:bg-gray-200 transition-all duration-200"
                >
                  Try Different Email
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
